/**
 * Live tier reveal (§4.2 D, §13.2): winners from the COMMITTED list revealed one tier at a time with suspense
 * pacing, operator-controlled (the `revealed` counts come from the server/WebSocket). Calm mode lists all
 * revealed results with a current-tier heading. Never changes the outcome.
 */
import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import { cn } from '../cn';
import { useEnergy } from '../motion/MotionProvider';
import { pickPreset, tierRevealRow } from '../motion/presets';

export interface TierWinner {
  winId: string;
  /** Anonymised / initials-only by default (§2.4 Winner publicity). */
  displayName: string;
  /** Entry ID or ticket reference from the fairness bundle. */
  entryRef?: string;
}

export interface Tier {
  id: string;
  name: string;
  prizeText: string;
  /** Total winners in this tier (committed). */
  total: number;
  /** Winners revealed so far (operator-controlled). Length ≤ total. */
  winners: TierWinner[];
}

export interface TierRevealProps {
  tiers: Tier[];
  currentTierId: string | null;
  /** Shown when the draw is complete. */
  completeText?: string;
  fairnessHref?: string;
  /** Operator-only controls (console); participant views omit this. */
  operator?: { canReveal: boolean; onRevealNext: () => void; label?: string };
  heading?: ReactNode;
  className?: string;
}

function initials(name: string): string {
  const parts = name
    .replace(/[^\p{L}\p{N} .-]/gu, '')
    .split(/[\s.-]+/)
    .filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).slice(0, 2) || '•';
}

export function TierReveal({
  tiers,
  currentTierId,
  completeText = 'All winners have been revealed.',
  fairnessHref,
  operator,
  heading,
  className,
}: TierRevealProps) {
  const { energy, calm } = useEnergy();
  const row = pickPreset(tierRevealRow, energy);
  const current = tiers.find((t) => t.id === currentTierId);
  const complete = tiers.every((t) => t.winners.length >= t.total);
  return (
    <section className={cn('wd-tier', className)} aria-label="Draw results">
      {heading}
      <h2 className="wd-reveal-heading" aria-live="polite" data-testid="tier-heading">
        {complete
          ? completeText
          : current
            ? `Now revealing: ${current.name}`
            : 'Waiting for the draw operator'}
      </h2>
      {tiers.map((t) => {
        const pending = Math.max(0, t.total - t.winners.length);
        return (
          <div
            key={t.id}
            className="wd-tier__group"
            data-current={t.id === currentTierId ? 'true' : undefined}
          >
            <h3 className="wd-tier__heading">
              {t.name} <span className="wd-muted">· {t.prizeText}</span>
            </h3>
            <p className="wd-muted wd-small" style={{ margin: 0 }}>
              {t.winners.length} of {t.total} revealed
            </p>
            <ol className="wd-tier__winners">
              <AnimatePresence initial={!calm}>
                {t.winners.map((w) => (
                  <motion.li
                    key={w.winId}
                    className="wd-tier__winner"
                    variants={row.variants}
                    initial={row.initial}
                    animate={row.animate}
                    transition={row.transition}
                  >
                    <span className="wd-tier__who">
                      <span className="wd-tier__avatar" aria-hidden="true">
                        {initials(w.displayName)}
                      </span>
                      <span>{w.displayName}</span>
                    </span>
                    <span className="wd-tabular wd-tier__ref">
                      {w.entryRef ? <span className="wd-muted">{w.entryRef} · </span> : null}
                      <code>{w.winId}</code>
                    </span>
                  </motion.li>
                ))}
              </AnimatePresence>
              {pending > 0 ? (
                <li className="wd-tier__winner wd-tier__pending">{pending} more to be revealed</li>
              ) : null}
            </ol>
          </div>
        );
      })}
      {fairnessHref ? (
        <a href={fairnessHref} data-testid="tier-fairness" className="wd-btn" data-variant="secondary">
          Verify this draw
        </a>
      ) : null}
      {operator ? (
        <div className="wd-btn-group">
          <button
            type="button"
            className="wd-btn"
            data-variant="primary"
            onClick={operator.onRevealNext}
            aria-disabled={!operator.canReveal || complete ? true : undefined}
            disabled={!operator.canReveal || complete}
          >
            {operator.label ?? 'Reveal next'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
